/**
 * Replay records and reconstruction. Because the core engine is a pure,
 * seeded reducer, a finished game is fully described by its seed, both deck
 * builds, and the ordered action log — replaying those actions through
 * `reduce` reproduces every intermediate position exactly. This is the wire
 * shape served by GET /api/replays/:id and consumed by the replay viewer.
 */
import {
  createInitialState,
  reduce,
  type Action,
  type BoardConfig,
  type DeckBuild,
  type GameState,
  type PlayerId,
} from "../core";

export interface ReplayPlayer {
  readonly name: string;
  readonly build: DeckBuild;
  /** Account id when the player was signed in; null for guests. */
  readonly accountId: string | null;
}

export interface ReplayRecord {
  /** The game's id (GameState.id). */
  readonly id: string;
  readonly roomId: string | null;
  readonly seed: number;
  readonly config: BoardConfig;
  readonly players: readonly [ReplayPlayer, ReplayPlayer];
  readonly actions: readonly Action[];
  readonly winner: PlayerId | null;
  readonly ranked: boolean;
  readonly endedAt: number;
}

/** Metadata-only listing row (no action log) for game-history lists. */
export interface ReplaySummary {
  readonly id: string;
  readonly whiteName: string;
  readonly blackName: string;
  readonly winner: PlayerId | null;
  readonly ranked: boolean;
  readonly endedAt: number;
}

/** The position before any action was taken. */
export function replayInitialState(replay: ReplayRecord): GameState {
  return createInitialState({
    id: replay.id,
    seed: replay.seed,
    config: replay.config,
    players: [
      { name: replay.players[0].name, build: replay.players[0].build },
      { name: replay.players[1].name, build: replay.players[1].build },
    ],
  });
}

/**
 * Every position of the game, in order: index 0 is the initial state and
 * index i is the position after the first i actions. An action the engine
 * rejects (which would indicate a corrupted record) ends the sequence early.
 */
export function replayStates(replay: ReplayRecord): GameState[] {
  const states: GameState[] = [replayInitialState(replay)];
  let current = states[0]!;
  for (const action of replay.actions) {
    const result = reduce(current, action);
    if (!result.ok) break;
    current = result.state;
    states.push(current);
  }
  return states;
}
